/**
 * BlogIndex.jsx — /blog
 * Blog listing page with category filter and post cards.
 * Route: <Route path="/blog" element={<BlogIndex/>}/>
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BLOG_POSTS, CATEGORIES } from './blogPosts';

const T = {
  bg:     '#07080c',
  bg2:    '#0c0f18',
  bg3:    '#111520',
  surf:   '#161c2a',
  border: 'rgba(255,255,255,.07)',
  borderH:'rgba(255,255,255,.13)',
  white:  '#dce6fa',
  muted:  'rgba(220,230,250,.45)',
  dim:    'rgba(220,230,250,.22)',
  cyan:   '#00c8f0',
  blue:   '#4d8fff',
  sans:   '"Bricolage Grotesque", sans-serif;',
};

export default function BlogIndex() {
  const [activeTag, setActiveTag] = useState('all');
  const [search,    setSearch]    = useState('');

  const filtered = BLOG_POSTS.filter(p => {
    const matchTag  = activeTag === 'all' || p.tag === activeTag;
    const matchSearch = !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchTag && matchSearch;
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        ::placeholder { color:rgba(220,230,250,.22) !important; }
        input:focus { border-color:rgba(0,200,240,.4) !important; outline:none; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)} }
        .blog-card { animation:fadeUp .3s ease both; }
        .blog-card:hover .blog-card-title { color:${T.cyan} !important; }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans }}>

        {/* Nav */}
        <nav style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 40px', background:T.bg2,
          borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:10,
        }}>
          <Link to="/" style={{ fontSize:17, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
            Tylent<span style={{ color:T.cyan }}>ed</span>
          </Link>
          <div style={{ display:'flex', gap:20 }}>
            {[['/','/','Home'],['/tools','Tools'],['/tools/finance','Finance'],['/blog','Blog']].map(([path, label]) => (
              <Link key={path} to={path} style={{ fontSize:13.5, color:path==='/blog'?T.white:T.muted, textDecoration:'none' }}>
                {label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Hero */}
        <div style={{
          padding:'64px 40px 40px', maxWidth:1100, margin:'0 auto',
          borderBottom:`1px solid ${T.border}`,
        }}>
          <div style={{
            display:'inline-flex', alignItems:'center', gap:7,
            padding:'4px 13px', borderRadius:20, marginBottom:18,
            background:'rgba(0,200,240,.08)', border:'1px solid rgba(0,200,240,.2)',
            fontSize:11.5, fontWeight:700, color:T.cyan, textTransform:'uppercase', letterSpacing:'.08em',
          }}>
            📝 Blog
          </div>
          <h1 style={{
            fontSize:'clamp(2rem,4.5vw,3rem)', fontWeight:900,
            color:T.white, letterSpacing:'-.03em', lineHeight:1.08, marginBottom:12,
          }}>
            Guides on FIRE, Investing,<br/>and Tools That Matter
          </h1>
          <p style={{ fontSize:15, color:T.muted, maxWidth:560, lineHeight:1.75, marginBottom:28 }}>
            Practical Canadian personal finance content — FIRE calculators explained, real estate analysis, freelance rates, developer tools, and more.
          </p>

          {/* Search */}
          <input
            type="text"
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width:'100%', maxWidth:400, padding:'11px 16px',
              background:T.bg2, border:`1px solid ${T.border}`,
              borderRadius:10, color:T.white, fontSize:14,
              fontFamily:T.sans, transition:'border-color .15s',
            }}
          />
        </div>

        {/* Category filter */}
        <div style={{
          maxWidth:1100, margin:'0 auto', padding:'20px 40px 0',
          display:'flex', gap:8, flexWrap:'wrap',
        }}>
          <button
            onClick={() => setActiveTag('all')}
            style={{
              padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer',
              fontFamily:T.sans, fontSize:12.5, fontWeight:600, transition:'all .15s',
              background: activeTag==='all' ? T.cyan : 'rgba(255,255,255,.06)',
              color:       activeTag==='all' ? '#07080c' : T.muted,
            }}
          >All</button>
          {CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setActiveTag(cat.id)} style={{
              padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer',
              fontFamily:T.sans, fontSize:12.5, fontWeight:600, transition:'all .15s',
              background: activeTag===cat.id ? `${cat.color}20` : 'rgba(255,255,255,.06)',
              color:       activeTag===cat.id ? cat.color : T.muted,
              outline:     activeTag===cat.id ? `1px solid ${cat.color}50` : 'none',
            }}>
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Post grid */}
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'24px 40px 64px' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 0', color:T.muted, fontSize:14 }}>
              No articles found for "{search}". Try a different search term.
            </div>
          ) : (
            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',
              gap:20,
            }}>
              {filtered.map((post, i) => (
                <BlogCard key={post.slug} post={post} index={i}/>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer style={{
          borderTop:`1px solid ${T.border}`, padding:'24px 40px',
          background:T.bg2, textAlign:'center',
          fontSize:12, color:T.dim,
        }}>
          © 2026 Tylented Corp · Surrey, BC, Canada ·{' '}
          <Link to="/tools" style={{ color:T.muted, textDecoration:'none' }}>Free Tools</Link>
          {' · '}
          <Link to="/finance" style={{ color:T.muted, textDecoration:'none' }}>Finance Hub</Link>
        </footer>
      </div>
    </>
  );
}

function BlogCard({ post, index }) {
  const cat = CATEGORIES.find(c => c.id === post.tag);
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="blog-card"
      style={{
        display:'block', textDecoration:'none',
        padding:'22px 20px', borderRadius:12,
        border:`1px solid rgba(255,255,255,.07)`,
        background:'rgba(255,255,255,.02)',
        transition:'all .2s',
        animationDelay:`${index * 0.05}s`,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${cat?.color || '#00c8f0'}50`;
        e.currentTarget.style.background  = `${cat?.color || '#00c8f0'}06`;
        e.currentTarget.style.transform   = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,.07)';
        e.currentTarget.style.background  = 'rgba(255,255,255,.02)';
        e.currentTarget.style.transform   = 'none';
      }}
    >
      {/* Category + read time */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{
          padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700,
          background:`${cat?.color || '#00c8f0'}15`,
          color: cat?.color || '#00c8f0',
        }}>
          {post.icon} {post.category}
        </span>
        <span style={{ fontSize:11.5, color:'rgba(220,230,250,.3)' }}>
          {post.readTime} read
        </span>
      </div>

      {/* Title */}
      <h2 className="blog-card-title" style={{
        fontSize:16, fontWeight:800, color:'#dce6fa',
        letterSpacing:'-.01em', lineHeight:1.35, marginBottom:10,
        transition:'color .15s',
      }}>
        {post.title}
      </h2>

      {/* Excerpt */}
      <p style={{
        fontSize:13, color:'rgba(220,230,250,.5)',
        lineHeight:1.7, marginBottom:16,
        display:'-webkit-box', WebkitLineClamp:3,
        WebkitBoxOrient:'vertical', overflow:'hidden',
      }}>
        {post.excerpt}
      </p>

      {/* Date + CTA */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:11.5, color:'rgba(220,230,250,.25)' }}>
          {new Date(post.date).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' })}
        </span>
        <span style={{ fontSize:12.5, fontWeight:700, color: cat?.color || '#00c8f0' }}>
          Read →
        </span>
      </div>
    </Link>
  );
}
